import React, { PropsWithChildren, useCallback, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { ThemeModeContext, ThemeMode } from "./ThemeModeContext";

const STORAGE_KEY_PREFIX = "receiving_theme_mode";

function readStoredMode(key: string): ThemeMode | null {
	try {
		const stored = localStorage.getItem(key);
		return stored === "dark" || stored === "light" ? stored : null;
	} catch {
		return null;
	}
}

export const ThemeModeProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const { accounts } = useMsal();
	const accountId = accounts[0]?.homeAccountId;

	const [mode, setMode] = useState<ThemeMode>("light");

	// Once accountId resolves, load the per-user preference (reset to light if none stored)
	useEffect(() => {
		if (!accountId) return;
		const stored = readStoredMode(`${STORAGE_KEY_PREFIX}_${accountId}`);
		setMode(stored ?? "light");
	}, [accountId]);

	const toggleMode = useCallback(() => {
		if (!accountId) return;
		setMode((prev) => {
			const next: ThemeMode = prev === "light" ? "dark" : "light";
			try {
				localStorage.setItem(`${STORAGE_KEY_PREFIX}_${accountId}`, next);
			} catch {
				// ignore storage errors
			}
			return next;
		});
	}, [accountId]);

	return <ThemeModeContext.Provider value={{ mode, toggleMode }}>{children}</ThemeModeContext.Provider>;
};
